import { CButton, CButtonGroup, CCard } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilCart } from "@coreui/icons";
import { Trans, useTranslation } from "react-i18next";
import { RiskIcon } from "@scryerlabs/ui";
import { TradingStrategyDirection } from "../../api/hooks/useTradingStrategies";
import { Key } from "./HelpExampleKey";

/**
 * A worked example of the portfolio builder, keyed 1-13 so the article can name each part.
 *
 * Built from literals for the same reasons as HelpExampleStrategyPage: the real builder needs a current
 * revision and sends every answer to the API, and a help page can be the first page of a session. The
 * builder's own wording and classes are used, so the labels can't drift from the page; the dots and the
 * growth line are stand-ins, since the article names them rather than reading them.
 */

const MINUS = "−";

const TILES: [string, string, string][] = [
  ["+38%", "avgYear", "text-success"],
  [`${MINUS}6.4%`, "realDd", "text-danger"],
  ["", "trades", ""],
  [`${MINUS}2.9%`, "worstMonth", "text-danger"],
  ["84%", "monthsUp", ""],
  ["66%", "winRate", ""],
];

const STRATEGIES: [string, string, number, string, string, string][] = [
  ["MSTR", "Example Strategy A", 3, "Aggressive", "+71%", `${MINUS}19.6%`],
  ["ETHUSD", "Example Strategy B", 2, "Balanced", "+52%", `${MINUS}7.8%`],
  ["TSLA", "Example Strategy C", 3, "Aggressive", "+44%", `${MINUS}12.4%`],
  ["SPXL", "Example Strategy D", 1, "Conservative", "+18%", `${MINUS}5.1%`],
];

/** Other portfolios of the size chosen, in the chart's own units: drawdown across, profit up. */
const DOTS: [number, number][] = [
  [92, 48], [118, 70], [131, 52], [140, 88], [152, 64], [158, 104], [166, 80], [174, 58], [181, 118],
  [188, 92], [196, 72], [203, 128], [209, 100], [214, 84], [221, 140], [226, 110], [232, 96], [238, 124],
  [244, 150], [249, 106], [255, 132], [261, 88], [266, 146], [272, 118], [279, 160], [285, 136], [292, 152],
  [298, 120], [124, 96], [146, 122], [170, 136], [194, 152], [216, 164], [236, 170], [258, 172],
];

const START: [number, number] = [214, 112];
const STEP: [number, number] = [158, 82];
const MATCH: [number, number] = [104, 44];

export function HelpExamplePortfolioBuilder() {
  const { t } = useTranslation(["portfolioBuilder", "tradingStrategies"]);
  const path = [START, STEP, MATCH].map(([x, y]) => `${x},${y}`).join(" ");

  return (
    <figure className="help-example-card help-example-page help-example-builder">
      <div className="portfolio-builder">
        <div className="help-example-builder-board">
          <CCard className="portfolio-explore">
            <div className="portfolio-questions">
              <div className="portfolio-questions-top">
                <span className="portfolio-eyebrow">{t("questions.progress", { number: 2, total: 15 })}</span>
                <div className="portfolio-progress" aria-hidden>
                  <i style={{ width: "13%" }} />
                </div>
                <div className="portfolio-questions-actions">
                  <CButton size="sm" color="primary" variant="outline">
                    {t("questions.back")}
                  </CButton>
                  <CButton size="sm" color="primary" variant="outline">
                    {t("questions.skip")}
                  </CButton>
                  <CButton size="sm" color="primary" variant="outline">
                    {t("questions.startAgain")}
                  </CButton>
                  <CButton size="sm" color="primary" variant="outline">
                    {t("questions.hide")}
                  </CButton>
                  <Key n={3} />
                </div>
              </div>
              <div className="portfolio-question-text">
                What matters more to you?
                <Key n={1} />
              </div>
              <div className="portfolio-answers">
                <button type="button" className="portfolio-answer">
                  <span className="portfolio-answer-title">
                    Growing my money faster
                    <Key n={2} />
                  </span>
                  <span className="portfolio-answer-line">Bigger falls along the way are fine.</span>
                </button>
                <button type="button" className="portfolio-answer">
                  <span className="portfolio-answer-title">Keeping losses smaller</span>
                  <span className="portfolio-answer-line">I'll give up some growth to fall less.</span>
                </button>
              </div>
            </div>

            <div className="portfolio-controls">
              <span className="portfolio-control">
                {t("controls.strategies")}
                <Key n={4} />
                <CButtonGroup size="sm" className="btn-group-pill">
                  {[2, 3, 4, 5, 6].map((size) => (
                    <CButton key={size} color="primary" variant={size === 4 ? undefined : "outline"}>
                      {size}
                    </CButton>
                  ))}
                </CButtonGroup>
              </span>
              <span className="portfolio-control">
                {t("controls.instruments")}
                <Key n={5} />
                <CButton size="sm" shape="rounded-pill" color="secondary" variant="outline">
                  {t("controls.allInstruments")}
                </CButton>
              </span>
              <span className="portfolio-control">
                <CButton size="sm" color="primary" variant="outline">
                  {t("controls.hidePath")}
                </CButton>
              </span>
              <span className="portfolio-legend">
                <span>
                  <i className="portfolio-key-pick" />
                  {t("dots.yourMatch")}
                  <Key n={7} />
                </span>
                <span>
                  <i className="portfolio-key-path" />
                  {t("dots.yourPath")}
                  <Key n={8} />
                </span>
                <span>
                  <i className="portfolio-key-dot" />
                  {t("dots.others", { count: DOTS.length })}
                  <Key n={6} />
                </span>
              </span>
            </div>

            {/* A stand-in, not a rendered chart: the article names the parts, it does not read the
                figures, and real dots here would be another set of invented figures to keep honest. */}
            <div className="help-example-dots" aria-hidden>
              <svg viewBox="0 0 320 200" width="100%">
                {[40, 80, 120, 160].map((y) => (
                  <line key={`y${y}`} className="portfolio-chart-grid" x1={24} x2={312} y1={y} y2={y} />
                ))}
                {[96, 168, 240].map((x) => (
                  <line key={`x${x}`} className="portfolio-chart-grid" x1={x} x2={x} y1={16} y2={180} />
                ))}
                {DOTS.map(([x, y]) => (
                  <circle key={`${x},${y}`} className="portfolio-dot" cx={x} cy={y} r={3.2} />
                ))}
                <polyline className="portfolio-path-line" points={path} />
                <circle className="portfolio-path-dot is-start" cx={START[0]} cy={START[1]} r={4} />
                <circle className="portfolio-path-dot" cx={STEP[0]} cy={STEP[1]} r={4} />
                <text className="portfolio-path-label" x={START[0] + 8} y={START[1] - 7}>
                  {t("dots.start")}
                </text>
                <circle className="portfolio-pick-ring" cx={MATCH[0]} cy={MATCH[1]} r={9} />
                <circle className="portfolio-pick" cx={MATCH[0]} cy={MATCH[1]} r={5.5} />
                <text className="portfolio-chart-title" x={168} y={196} textAnchor="middle">
                  {t("dots.xAxis")}
                </text>
                <text className="portfolio-chart-title" transform="translate(11 98) rotate(-90)" textAnchor="middle">
                  {t("dots.yAxis")}
                </text>
              </svg>
            </div>
          </CCard>

          <CCard className="portfolio-panel">
            <section>
              <div className="portfolio-panel-head">
                <span className="portfolio-eyebrow">{t("panel.yourMatch")}</span>
              </div>
              <div className="portfolio-panel-title">
                {t("panel.title", {
                  count: 4,
                  side: t(`tradingStrategies:sides.${TradingStrategyDirection.LongAndShort}`),
                })}
                <Key n={9} />
              </div>
              <p className="portfolio-panel-split small text-body-secondary mb-0">
                {t("panel.split", { share: "25%" })}
              </p>
              <div className="portfolio-tiles">
                {TILES.map(([value, key, tone]) => (
                  <div key={key} className="portfolio-tile">
                    <div className={`portfolio-tile-value ${tone}`}>
                      {key === "trades" ? t("panel.trades.perWeek", { count: 2 }) : value}
                    </div>
                    <div className="portfolio-tile-label">{t(`panel.figures.${key}`)}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="portfolio-what-if">
              <div className="portfolio-what-if-ask">
                {t("whatIf.putIn")}{" "}
                <span className="portfolio-amount">
                  {t("whatIf.amountPrefix")}
                  <input readOnly tabIndex={-1} value="10" aria-hidden />
                  {t("whatIf.amountSuffix")}
                </span>{" "}
                {t("whatIf.atTheEndOf")} <strong className="text-primary">Sep 2024</strong>
                <Key n={10} />
              </div>
              <div className="portfolio-what-if-result">
                <span className="portfolio-what-if-label">{t("whatIf.worth")}</span>
                <strong>$19,044</strong>
                <span className="text-success">{t("whatIf.gain", { gain: "+90%", month: "Sep 2026" })}</span>
              </div>
              <CButtonGroup size="sm" className="btn-group-pill">
                <CButton color="primary" variant="outline">
                  {t("whatIf.quick.oneYear")}
                </CButton>
                <CButton color="primary">{t("whatIf.quick.years", { count: 2 })}</CButton>
                <CButton color="primary" variant="outline">
                  {t("whatIf.quick.years", { count: 3 })}
                </CButton>
                <CButton color="primary" variant="outline">
                  {t("whatIf.quick.years", { count: 5 })}
                </CButton>
                <CButton color="primary" variant="outline">
                  {t("whatIf.quick.since", { year: 2019 })}
                </CButton>
              </CButtonGroup>
              <div className="portfolio-growth help-example-growth" aria-hidden>
                <svg viewBox="0 0 300 100" preserveAspectRatio="none">
                  <path className="portfolio-growth-area" d="M0,92 L40,86 L80,80 L120,74 L160,60 L200,52 L240,34 L270,22 L300,8 L300,100 L0,100 Z" />
                  <path
                    className="portfolio-growth-line"
                    d="M0,92 L40,86 L80,80 L120,74 L160,60 L200,52 L240,34 L270,22 L300,8"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              </div>
              <p className="portfolio-note mb-0">{t("whatIf.biggestFall", { fall: `${MINUS}6.2%` })}</p>
            </section>

            <section>
              <span className="portfolio-eyebrow">
                {t("panel.strategies")}
                <Key n={11} />
              </span>
              <ul className="portfolio-strategies">
                {STRATEGIES.map(([ticker, name, riskProfile, risk, avgYear, drawdown]) => (
                  <li key={name} className="portfolio-strategy">
                    <span className="portfolio-ticker">{ticker}</span>
                    <div className="portfolio-strategy-body">
                      <div className="portfolio-strategy-name">{name}</div>
                      <div className="portfolio-strategy-sub">
                        <span className="portfolio-strategy-risk">
                          <RiskIcon riskProfile={riskProfile} label={risk} size={13} />
                          {risk}
                        </span>
                        <span>
                          <Trans
                            t={t}
                            i18nKey="panel.onItsOwn"
                            values={{ avgYear, drawdown }}
                            components={{ gain: <span className="text-success" />, fall: <span className="text-danger" /> }}
                          />
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <section className="portfolio-buy">
              <div className="portfolio-buy-row">
                <span className="portfolio-total">
                  $76{" "}
                  <small>
                    {t("panel.perMonth")} · {t("panel.breakdown", { count: 4, price: "$19" })}
                  </small>
                  <Key n={12} />
                </span>
                <CButtonGroup size="sm" className="btn-group-pill">
                  <CButton color="primary">{t("panel.monthly")}</CButton>
                  <CButton color="primary" variant="outline">
                    {t("panel.yearly")}
                  </CButton>
                </CButtonGroup>
              </div>
              <CButton color="primary" className="w-100 portfolio-add">
                <CIcon icon={cilCart} className="me-2" />
                {t("panel.add", { count: 4 })}
              </CButton>
              <p className="portfolio-fine mb-0">
                {t("panel.fine", { period: "Jan 2019 – Sep 2026" })}
                <Key n={13} />
              </p>
            </section>
          </CCard>
        </div>
      </div>

      <figcaption>
        An example. <strong>Not real strategies</strong> — the names and figures are invented.
      </figcaption>
    </figure>
  );
}
